import type { ApolloError } from '@apollo/client';
import { CAPTCHA_REQUIRED, CAPTCHA_STATUS_CODE } from '@kompare/captcha-react';
import type { GraphQLFormattedError } from 'graphql';

/**
 * GraphQL side of the reactive captcha contract (`@kompare/rate-limit` README,
 * "Conditional captcha"). property-api answers a challenge as an HTTP 200 whose error
 * carries `extensions: { code: 'CAPTCHA_REQUIRED', statusCode: 428 }` — there is no
 * HTTP 428 to read, so this maps the error back to the status the
 * `useCaptchaChallenge` hook expects.
 *
 * Accepts either the thrown ApolloError (errorPolicy 'none', the client default) or
 * the `errors` array of a resolved result (errorPolicy 'all').
 */
export function captchaStatusOf(
  source: ApolloError | readonly GraphQLFormattedError[] | undefined | null,
): number | undefined {
  const errors = Array.isArray(source)
    ? (source as readonly GraphQLFormattedError[])
    : (source as ApolloError | undefined | null)?.graphQLErrors;
  const hit = errors?.find((e) => e.extensions?.code === CAPTCHA_REQUIRED);
  return hit ? Number(hit.extensions?.statusCode ?? CAPTCHA_STATUS_CODE) : undefined;
}

/**
 * Numeric outcome of a submit for the RUM `submit_status` label, so the dashboards
 * built for the REST funnels (ao-checkout) apply here too: CAPTCHA_REQUIRED → 428,
 * RATE_LIMITED → 429, IP_BLOCKED → 403, any other GraphQL error → 500, a transport
 * failure → its status (0 when there is none), success → 200.
 */
export function submitStatusOf(error: ApolloError | undefined | null): number {
  if (!error) return 200;
  const code = String(error.graphQLErrors?.[0]?.extensions?.code ?? '');
  if (code === CAPTCHA_REQUIRED) return CAPTCHA_STATUS_CODE;
  if (code === 'RATE_LIMITED') return 429;
  if (code === 'IP_BLOCKED') return 403;
  if (error.graphQLErrors?.length) return 500;
  return (error.networkError as { statusCode?: number } | null | undefined)?.statusCode ?? 0;
}
